import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getAdminPerformanceMetrics,
  getAdminRankings,
  getAdminScoringSettings,
  AdminPerformanceMetricsData,
  AdminRankingsData,
  AdminScoringSettingsData,
} from "../Actions/AdminProfessionalScoringActions";

interface AdminProfessionalScoringState {
  // Performance Metrics
  performanceMetrics: AdminPerformanceMetricsData | null;
  performanceMetricsLoading: boolean;
  performanceMetricsError: string | null;

  // Rankings
  rankings: AdminRankingsData | null;
  rankingsLoading: boolean;
  rankingsError: string | null;

  // Scoring Settings
  scoringSettings: AdminScoringSettingsData | null;
  scoringSettingsLoading: boolean;
  scoringSettingsError: string | null;
}

const initialState: AdminProfessionalScoringState = {
  // Performance Metrics
  performanceMetrics: null,
  performanceMetricsLoading: false,
  performanceMetricsError: null,

  // Rankings
  rankings: null,
  rankingsLoading: false,
  rankingsError: null,

  // Scoring Settings
  scoringSettings: null,
  scoringSettingsLoading: false,
  scoringSettingsError: null,
};

const adminProfessionalScoringSlice = createSlice({
  name: "adminProfessionalScoring",
  initialState,
  reducers: {
    // Reset actions
    resetPerformanceMetrics: (state) => {
      state.performanceMetrics = null;
      state.performanceMetricsError = null;
    },
    resetRankings: (state) => {
      state.rankings = null;
      state.rankingsError = null;
    },
    resetScoringSettings: (state) => {
      state.scoringSettings = null;
      state.scoringSettingsError = null;
    },
  },
  extraReducers: (builder) => {
    // Performance Metrics
    builder
      .addCase(getAdminPerformanceMetrics.pending, (state) => {
        state.performanceMetricsLoading = true;
        state.performanceMetricsError = null;
      })
      .addCase(
        getAdminPerformanceMetrics.fulfilled,
        (state, action: PayloadAction<AdminPerformanceMetricsData>) => {
          state.performanceMetricsLoading = false;
          state.performanceMetrics = action.payload;
          state.performanceMetricsError = null;
        }
      )
      .addCase(
        getAdminPerformanceMetrics.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.performanceMetricsLoading = false;
          state.performanceMetricsError = action.payload || "Failed to fetch performance metrics";
        }
      );

    // Rankings
    builder
      .addCase(getAdminRankings.pending, (state) => {
        state.rankingsLoading = true;
        state.rankingsError = null;
      })
      .addCase(
        getAdminRankings.fulfilled,
        (state, action: PayloadAction<AdminRankingsData>) => {
          state.rankingsLoading = false;
          state.rankings = action.payload;
          state.rankingsError = null;
        }
      )
      .addCase(
        getAdminRankings.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.rankingsLoading = false;
          state.rankingsError = action.payload || "Failed to fetch rankings";
        }
      );

    // Scoring Settings
    builder
      .addCase(getAdminScoringSettings.pending, (state) => {
        state.scoringSettingsLoading = true;
        state.scoringSettingsError = null;
      })
      .addCase(
        getAdminScoringSettings.fulfilled,
        (state, action: PayloadAction<AdminScoringSettingsData>) => {
          state.scoringSettingsLoading = false;
          state.scoringSettings = action.payload;
          state.scoringSettingsError = null;
        }
      )
      .addCase(
        getAdminScoringSettings.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.scoringSettingsLoading = false;
          state.scoringSettingsError = action.payload || "Failed to fetch scoring settings";
        }
      );
  },
});

export const {
  resetPerformanceMetrics,
  resetRankings,
  resetScoringSettings,
} = adminProfessionalScoringSlice.actions;

export default adminProfessionalScoringSlice.reducer;
